import { animate, AnimationEvent, keyframes, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';

import * as L from 'leaflet';
import 'leaflet.markercluster';
import { MenuItem, MessageService } from 'primeng/api';
import { combineLatest, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { switchMap, map, filter, catchError } from 'rxjs/operators';

import { EmploymentChange } from '@Common/RhpContexts/components/ContactCard/contact-card.component';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { TargetingQueryApi } from '../../../../api/targeting/model/targeting-search.api.model';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import {
    BulkActionConfirmationDialogComponent
} from '../../../../Common/Dialogs/components/BulkActionConfirmationDialog/bulk-action-confirmation-dialog.component';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { FilterChipsComponent } from '../../../../Common/Filter/components/filterChips/filter-chips.component';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { AppearCondition, AutocompleteCustomOption } from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { AutocompleteComponent } from '../../../../Common/Forms/components/Autocomplete/autocomplete.component';
import { ChipContainerComponent } from '../../../../Common/Forms/components/ChipContainer/chip-container.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ChipModel } from '../../../../Common/Forms/models/chip.model';
import { RightSidebarComponent } from '../../../../Common/Layout/components/RightSidebar/right-sidebar.component';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ListPickerDialogComponent } from '../../../../Common/RhpContexts/components/ListPickerDialog/list-picker-dialog.component';
import { EntityTakeoverModel } from '../../../../Common/RhpContexts/models/entity-takeover.model';
import { ListPickerService } from '../../../../Common/RhpContexts/services/list-picker.service';
import {
    TargetingBatchActionsDialogComponent,
    TargetingBatchActionsEnum
} from '../../../../Common/TargetingShared/components/TargetingBatchActionsDialog/targeting-batch-actions-dialog.component';
import { SearchModeEnum } from '../../../../Common/TargetingShared/enum/search-mode.enum';
import { ContactService } from '../../../../Common/TargetingShared/services/contact-service';
import { TargetingPreferencesService } from '../../../../Common/TargetingShared/services/targeting-preferences.service';
import { TargetingRhpContextsService } from '../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { TargetingRhpDialogService } from '../../../../Common/TargetingShared/services/targeting-rhp-dialog.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { DiscoverRouteTypeEnum } from '../../../DiscoverRouter/enums/discover-route-type.enum';
import { DiscoverRouteModel } from '../../../DiscoverRouter/models/discover-route.model';
import { DiscoverRoutingService } from '../../../DiscoverRouter/services/discover-routing.service';
import { BaseJournodexEntityModel } from '../../models/base-journodex-entity.model';
import { TargetingListList } from '../../models/targeting-list-list.model';
import { TargetingList } from '../../models/targeting-list.model';
import { TargetingResultModel } from '../../models/targeting-result.model';
import { TargetingSearch } from '../../models/targeting-search.model';
import { SourceType } from '../../services/source-picker-service';
import { TargetingListService } from '../../services/targeting-list.service';
import { TargetingSearchService } from '../../services/targeting-search.service';
import { TargetingSearchesService } from '../../services/targeting-searches.service';
import { targetingAddToOptions } from '../../../../Discover/Targeting/models/targeting-add-to-options.model';
import { TargetingAddToTypeEnum } from '../../../../Discover/Targeting/enums/targeting-add-to-type.enum';
import { DistributionService } from '../../../../Common/TargetingShared/services/distribution.service';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { DistributionSummaryModel } from '../../../../Common/TargetingShared/model/distribution-summary-model';
import { DistributionStatus } from '../../../../Common/TargetingShared/enum/distribution-status.enum';
import { DistributionPickerService } from '../../../../Common/RhpContexts/services/distribution-picker.service';
import { DistPickerDialogComponent } from '../../../../Common/RhpContexts/components/DistPickerDialog/dist-picker-dialog.component';
import { toolbarAnimation } from '../../../../Common/Misc/animations/toolbar-animation';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { CommonApiErrorEnum } from '../../../../api/genericApi/enums/common-api-error.enum';
import { RelationsViewModesEnum } from '@Monitor/Shared/enums/relations-view-modes.enum';
import { ToolbarStateService } from '@Common/TargetingShared/services/toolbar-state.service';
import { TargetingSearchPendoEventEnum } from '../../enums/targeting-search-pendo-event.enum';


class SearchErrors {
    hasNoSearchName: boolean = false;
    searchNameDuplicate: boolean = false;
}

@Component({
    selector: 'agpr-targeting-results',
    templateUrl: './targeting-results.component.html',
    styleUrls: ['./targeting-results.component.scss'],
    providers: [
        ToolbarStateService,
        {
            provide: 'PaginatorServiceInterface',
            useExisting: TargetingSearchService
        },
        {
            provide: FilterService,
            useExisting: TargetingSearchService
        },
        {
            provide: 'SortOrderServiceInterface',
            useExisting: TargetingSearchService
        },
        {
            provide: 'MainSearchListPickerService',
            useClass: ListPickerService
        },
        {
            provide: ListPickerService,
            useExisting: 'MainSearchListPickerService'
        },
        {
            provide: 'MainSearchDistributionPickerService',
            useClass: DistributionPickerService
        },
        {
            provide: DistributionPickerService,
            useExisting: 'MainSearchDistributionPickerService'
        }
    ],
    animations: [
        trigger('fade', [
            transition('* => true', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 0
                    }),
                    style({
                        opacity: 1
                    })
                ]))
            ]),
            transition('true => *', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 1
                    }),
                    style({
                        opacity: 0
                    })
                ]))
            ])
        ]),
        toolbarAnimation
    ],
    standalone: false
})
export class TargetingResultsComponent implements OnDestroy, AfterViewInit, OnInit {
    id: string = 'targetingResults';

    @ViewChild('sidebar') public rightSidebar: RightSidebarComponent;
    @ViewChild('listViewPanel') public listViewPanel: ScrollPanelComponent;
    @ViewChild('mapContainer') public mapContainer: ElementRef;
    @ViewChild('listChipsComponent') public listChipsComponent: ChipContainerComponent;
    @ViewChild('filterChipsComponent') public filterChipsComponent: FilterChipsComponent;
    @ViewChild('listBuilderDropdown') public listBuilderDropdown: AutocompleteComponent;

    // Lng set to 500 as worldCopyJump is enabled
    readonly MAP_MAX_BOUNDS = new L.LatLngBounds(new L.LatLng(-90, -500), new L.LatLng(90, 500));
    readonly MAP_MAX_ZOOM = 12;

    readonly buttonTypes = ButtonTypesEnum;
    readonly routeType = DiscoverRouteTypeEnum;
    readonly entityTypes = EntityType;
    readonly messageSeverityEnum = messageSeverityEnum;

    leafletMap: L.Map;
    leafletMarkers: L.MarkerClusterGroup;
    excludedMarkers: Set<L.LatLng> = new Set();
    rateLimitExceeded: boolean = false;

    searchLoading = false;
    deleteLoading = false;
    removeFromListLoading = false;
    filtersLoading = false;

    errors: SearchErrors = new SearchErrors();

    total: number = 0;
    mapViewTotal: number = 0;

    currentRoute: DiscoverRouteModel;
    targetingSearch: TargetingSearch;
    targetingList: TargetingList;

    results: TargetingResultModel[];
    focusedResult: TargetingResultModel;
    selectedResults: TargetingResultModel[] = [];
    addedResults: TargetingResultModel[] = [];

    geocodeResults: L.LatLng[];
    oldGeocodeResults: string[] = [];

    ownedTargetingLists: TargetingListList[];

    filtersLocked: boolean = false;
    workingListsLocked: boolean = false;
    showMapResetButton: boolean = null;
    outletDetailTabIndex = 1;

    selectedListsChips: ChipModel[] = [];
    selectedLists: TargetingListList[] = [];
    selectedListsTooltip: string;
    targetingLists$: Observable<TargetingListList[]>;
    freshCreatedId: number;
    listHasActiveNotices: boolean = false;
    activeNoticeFilterApplied: boolean = false;

    contactEditorVisible: boolean = false;
    contactImporterVisible: boolean = false;
    rhpSelectedTabIndex: number = 0;

    searchDirty = false;

    allSelected: boolean = false;

    rhpTakeOver: boolean = false;
    profileEntity: BaseJournodexEntityModel;
    savedResultsEntity: EntityType;

    isPortfolioEditor = false;
    portfolioView = false;

    hasUnavailableOutlet: boolean = false;

    batchActionsEnum = TargetingBatchActionsEnum;

    viewModeOptions: DropdownOptionModel<RelationsViewModesEnum>[] = [
        new DropdownOptionModel('',RelationsViewModesEnum.CARD,'','fa-list'),
        new DropdownOptionModel('',RelationsViewModesEnum.MAP,'','fa-location-dot')
    ];

    selectedViewMode: RelationsViewModesEnum = this.targetingSearchService.mapView ? RelationsViewModesEnum.MAP : RelationsViewModesEnum.CARD;

    navHeightObserver = new ResizeObserver((entry) => {
        const navHeight = entry[0].contentRect.height;
        this.cd.detectChanges();
    });

    readonly listBuilderOptions: AutocompleteCustomOption[] = [{
        label: 'Discover.listBuilder.createList',
        action: (listName: string) => {
            if (listName.length === 0) {

                return;
            }

            this.targetingListService.isNameDuplicated$(listName).pipe(
                switchMap((duplicate) => {
                    if (duplicate) {
                        throw new Error(CommonApiErrorEnum.duplicateName);
                    } else {
                        const list = new TargetingList({name: listName});
                        return this.targetingListService.create(list);
                    }
                })
            ).subscribe({
                next: (id) => {
                    this.freshCreatedId = id;
                    this.newlyAddedList();
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.dialogs.newList.duplicate',
                    });
                }
            });
        },
        appearCondition: AppearCondition.noExactMatchInSuggestions,
        clearOnSelect: false
    }];


    private subscriptions: Subscription[] = [];
    private pagingDialogKey: string = null;

    readonly saveActions = [
        {
            label: this.localizationService.transform('Discover.targeting.actions.saveChanges'),
            command: () => this.saveSearch()
        },
        {
            label: this.localizationService.transform('Discover.targeting.actions.saveAsNewSearch'),
            command: () => this.openCreateDialog()
        }
    ];

    readonly actions: MenuItem[] = [
        {
            label: 'Discover.batchActions.addToList',
            value: this.batchActionsEnum.addToList,
            command: () => {
                this.openBatchActions(this.batchActionsEnum.addToList);
            }
        },
        {
            label: 'Discover.batchActions.removeFromList',
            value: this.batchActionsEnum.removeFromList,
            command: () => {
                this.openBatchActions(this.batchActionsEnum.removeFromList);
            }
        },
        {
            label: 'Discover.batchActions.export',
            value: this.batchActionsEnum.export,
            command: () => this.openBatchActions(this.batchActionsEnum.export)
        },
        {
            label: 'Discover.batchActions.exportInteractions',
            value: this.batchActionsEnum.exportInteractions,
            command: () => this.openBatchActions(this.batchActionsEnum.exportInteractions)
        },
        {
            label: 'Discover.batchActions.distribute',
            value: this.batchActionsEnum.distribute,
            command: () => this.openBatchActions(this.batchActionsEnum.distribute)
        }
    ];

    pageHeader: string;
    pageSrHeader: string;

    readonly targetingAddToOptions = targetingAddToOptions;
    readonly targetingAddToTypes = TargetingAddToTypeEnum;
    readonly maxValueOfContactForMapView = 10_000;
    addToType: TargetingAddToTypeEnum = TargetingAddToTypeEnum.LIST;
    distributions: DropdownOptionModel<DistributionSummaryModel>[];
    distributions$: Observable<DistributionSummaryModel[]>;

    selectedDistributionsChips: ChipModel[] = [];
    selectedDistributions: DistributionSummaryModel[] = [];
    selectedDistributionsTooltip: string;
    workingDistributionsLocked: boolean = false;

    constructor(
        private configService: ConfigService,
        public targetingSearchService: TargetingSearchService,
        private targetingSearchesService: TargetingSearchesService,
        public targetingListService: TargetingListService,
        private targetingRhpContextsService: TargetingRhpContextsService,
        private discoverRoutingService: DiscoverRoutingService,
        public targetingRhpDialogService: TargetingRhpDialogService,
        private targetingPreferencesService: TargetingPreferencesService,
        private contactService: ContactService,
        private coreDialogService: CoreDialogService,
        private messageService: MessageService,
        private injector: Injector,
        public localizationService: LocalizationService,
        @Inject('MainSearchListPickerService') public listPickerService: ListPickerService,
        public cd: ChangeDetectorRef,
        public titleService: Title,
        public distributionService: DistributionService,
        @Inject('MainSearchDistributionPickerService') public distributionPickerService: DistributionPickerService,
        private userPreferencesService: UserPreferencesService,
        public toolbarStateService: ToolbarStateService
    ) {
        this.targetingLists$ = this.targetingListService.all$;
        listPickerService.useLocking = true;
        listPickerService.loadWorkingListsFromSession();

        distributionPickerService.useLocking = true;
        distributionPickerService.loadWorkingDistributionsFromSession();
        this.distributions$ = this.distributionService.getDistributionSummaries().pipe(
            filter(x => !!x),
            map(dist => dist.filter(element => element.status === DistributionStatus.draft))
        );
        this.subscriptions.push(
            this.discoverRoutingService.getCurrentRoute().subscribe(
                route => {
                    this.currentRoute = route;
                    this.mapViewTotal = null;

                    if (route.type === DiscoverRouteTypeEnum.portfolioResults) {
                        this.portfolioView = true;
                        if (route.value) {
                            const list = new TargetingList();
                            list.id = +route.value;
                            this.targetingList = list;
                            this.targetingSearchService.setActiveList(list);
                            this.targetingSearchService.doSearch();
                        }
                    }
                    if (route.type === DiscoverRouteTypeEnum.privateContacts) {
                        this.pageHeader = this.localizationService.transform('Discover.pageHeader.privateContacts');
                        this.pageSrHeader = 'results';
                    }
                }
            ),
            this.targetingSearchService.getSearchLoading().subscribe(loading => this.searchLoading = loading),
            this.targetingSearchService.getFiltersLoading().subscribe(loading => this.filtersLoading = loading),
            this.targetingSearchService.getFiltersLocked().subscribe(locked => this.filtersLocked = locked),
            this.targetingSearchService.hasActiveNoticeFilterApplied().subscribe(hasActiveNotices => this.activeNoticeFilterApplied = hasActiveNotices),
            this.targetingSearchesService.currentResource$.subscribe(
                search => {
                    this.selectNone();
                    this.targetingSearchService.clearProfileEntity();

                    this.targetingSearch = search;
                    if (search?.id) {
                        this.titleService.setTitle(
                            this.localizationService.transform('Discover.savedSearch.browserTitle.name', {name: search.name})
                        );
                        this.pageHeader =  this.targetingSearch.name;
                        this.pageSrHeader = 'Discover.pageHeader.savedSearch';
                    }
                }
            ),
            this.targetingSearchesService.unavailableOutlet$.subscribe(value => this.hasUnavailableOutlet = value),
            this.targetingListService.currentResource$.subscribe(
                list => {
                    this.selectNone();
                    this.targetingSearchService.clearProfileEntity();

                    this.targetingList = list;
                    if (list) {
                        this.hideFocusedListFromDropdown();
                        this.selectedResults = [];
                        this.titleService.setTitle(
                            this.localizationService.transform('Discover.lists.browserTitle.name', {name: list.name})
                        );
                        this.pageHeader = this.targetingList.name;
                        this.pageSrHeader = 'Discover.pageHeader.targetedList';
                    }
                }
            ),
            this.targetingSearchService.getItemCount().subscribe(count => {
                this.total = count;
            }),
            combineLatest([
                this.targetingListService.currentResource$.pipe(
                    filter(list => !!list)
                ),
                this.targetingSearchService.getResults().pipe(
                    filter(results => results?.length > 0)
                )
            ]).subscribe(() => {
                this.updateActiveNotices();
            }),
            this.targetingSearchService.getResults().subscribe(x => {
                this.results = x;
                this.addedResults = [];
                if (this.listViewPanel) {
                    this.listViewPanel.scrollTo(0, 0, false);
                }
                this.selectNone();
                this.removeFromListLoading = false;

                // To identify the entity type of saved searches and will be used for a tooltip's condition
                if (x !== null) {
                    x.forEach(result => this.savedResultsEntity = result.entityType);
                }
                this.hideShowRemoveDistribution();
            }),
            this.targetingListService.all$.subscribe(lists => {
                this.ownedTargetingLists = lists;
                this.hideFocusedListFromDropdown();

                // remove lists that have been deleted
                this.selectedLists = this.selectedLists.filter((selectedList) => {
                    return !!lists.find(list => selectedList.id === list.id);
                });
                this.updateSelectedListsTooltip();

                this.newlyAddedList();
            }),
            this.listPickerService.getWorkingLists().pipe(
                filter(x => !!x))
                .subscribe(lists => {
                    this.selectedLists = lists;
                    this.selectedListsChips = lists.map(list => ({
                        label: list.name
                    }));
                    this.updateSelectedListsTooltip();
                }),
            this.listPickerService.getWorkingListsLock().subscribe(lock => {
                this.workingListsLocked = lock;
            }),
            this.distributionPickerService.getWorkingDistributions().pipe(
                filter(x => !!x))
                .subscribe(distributions => {
                    this.selectedDistributions = distributions;
                    this.selectedDistributionsChips = distributions.map(list => ({
                        label: list.name
                    }));
                    this.updateSelectedDistributionsTooltip();
                    this.hideShowRemoveDistribution();
                }),
            this.distributionPickerService.getWorkingDistributionsLock().subscribe(lock => {
                this.workingDistributionsLocked = lock;
                if (lock) {
                    this.addToType = TargetingAddToTypeEnum.DISTRIBUTION;
                }
            }),
            this.targetingRhpContextsService.getCurrentTab().subscribe(tab => {
                if (tab === null) {
                    this.rhpSelectedTabIndex = 0;
                } else {
                    this.rhpSelectedTabIndex = tab;
                }
                if (this.rightSidebar) {
                    this.rightSidebar.contentScrollPanel.scrollTo(0, 0, false);
                }
            }),
            this.targetingSearchService.getPageCriteria().subscribe(changed => {
                this.selectNone();
            }),
            this.targetingSearchesService.resourceDirty$.subscribe(x => this.searchDirty = x),
            this.contactService.getDeleteLoading().subscribe(deleteLoading => {
                // We can infer that a delete was underway and has now completed,
                // so the selected entities should be cleared since they dont exist any longer
                if (this.deleteLoading !== deleteLoading && !deleteLoading) {
                    this.selectedResults = [];
                }
                this.deleteLoading = deleteLoading;
            }),
            this.contactService.getProfileEntity().subscribe(entity => this.profileEntity = entity),
            this.contactService.getEditingContact().subscribe(contact => {
                if (contact) {
                    const model = this.contactService.convertNewContactToTargetingResultModel(contact);
                    const resultIndex = this.results?.findIndex(result => result.id === model.id);
                    const newResultIndex = this.addedResults?.findIndex(x => x.id === model.id);

                    if (resultIndex > -1) {
                        this.results[resultIndex] = model;
                        this.results = this.results.slice();
                    } else if (newResultIndex > -1) {
                        this.addedResults[newResultIndex] = model;
                        this.addedResults = this.addedResults.slice();
                    }
                    this.targetingRhpContextsService.setContact(contact.id);
                }
            }),
            this.contactService.getNewPrivateContact().subscribe(contactEntity => {
                this.addedResults.push(contactEntity);
                this.targetingSearchService.setFocusedResult(contactEntity);
            }),
            this.targetingPreferencesService.targetingPreferences$.pipe(filter(x => !!x)).subscribe(prefs => {
                this.isPortfolioEditor = prefs.isPortfolioEditor;
            }),
            this.distributionService.getDistributionSummaries().pipe(filter(x => !!x)).subscribe(distros => {
                this.distributions = distros
                    .filter(x => x.status === DistributionStatus.draft)
                    .map(x => new DropdownOptionModel(x.name, x));
                this.distributions.sort((x1, x2) => x1.value.name.localeCompare(x2.value.name));
            }),
            this.userPreferencesService.getUserAppPreferences().subscribe(prefs => {
                this.toolbarStateService.setToolbarAnimationDisabled(!prefs.generalPrefs.accessibility.collapseToolbars);
            })
        );

        this.targetingSearchService.registerPagingValidator(this.pagingValidator.bind(this));
    }

    ngOnInit() {
        const nav = document.querySelector('.agpr-masterLayout-navigation');
        this.navHeightObserver.observe(nav);
    }

    ngAfterViewInit() {
        if (this.targetingSearchService.mapView) {
            this.targetingSearchService.rectangleQuery =  {
                lowerLeftCorner: '-90,-180',
                upperRightCorner: '90,180'
            };
            this.targetingSearchService.setPageCriteria({page: 1, pageSize: 25}, false);
            this.targetingSearchService.doSearch();
        }
        // mapContainer element must be rendered before initializing map
        this.subscriptions.push(
            this.targetingSearchService.getGeoCodeResults().subscribe(results => {

                if (!results) {

                    return;
                }

                this.geocodeResults = results.map(x => this.toLeafletLatLong(x));
                this.mapViewTotal = results.length;

                const isNewCoords = (!!this.oldGeocodeResults) ? !results.every(geoCode => !!this.oldGeocodeResults?.includes(geoCode)) : true;

                // only reinitialize when new results found or count changed
                if (!!results && (isNewCoords || this.oldGeocodeResults?.length !== results.length)) {
                    this.initializeMap();
                }

                this.oldGeocodeResults = results;
            })
        );
    }

    newlyAddedList() {
        if (this.freshCreatedId) {
            const freshCreatedList = this.ownedTargetingLists.find(x => x.id === this.freshCreatedId);
            this.listPickerService.addWorkingList(freshCreatedList);
            this.listPickerService.setWorkingListsLock(true);
            this.freshCreatedId = undefined;
        }
    }

    hideFocusedListFromDropdown() {
        if (this.ownedTargetingLists && this.targetingList) {
            this.ownedTargetingLists = this.ownedTargetingLists.filter(x => x.id !== this.targetingList.id);
        }
    }

    editSearch(): void {
        // we do not want the map information when we go back to edit a search, it gives the user the wrong count
        delete this.targetingSearch.rectangleQuery;

        if (this.currentRoute.type === DiscoverRouteTypeEnum.targetingResults) {
            this.discoverRoutingService.gotoTargetingSearch(null, true);
        } else if (this.currentRoute.type === DiscoverRouteTypeEnum.targetingSavedSearchResults) {
            this.discoverRoutingService.gotoTargetingSavedSearch(this.targetingSearch.id);
        }
    }

    // The results get their listMembership added on async, this makes the difference show up to angular
    trackResult(index: number, result: TargetingResultModel) {
        return JSON.stringify({
            id: result.id,
            listMemberships: result.listMemberships
        });
    }

    pagingValidator(): Observable<boolean> {
        if (this.selectedResults.length > 0 && !this.pagingDialogKey) {
            const pagingValidationSubject = new Subject<boolean>();
            const io: ConfirmDialogModel = {
                inputs: {
                    heading: 'Discover.targeting.result.paginator.selectedContactWarningHeader',
                    message: 'Discover.targeting.result.paginator.selectedContactWarningMessage',
                },
                outputs: {
                    dialogClose: (confirm: boolean) => {
                        this.pagingDialogKey = null;
                        pagingValidationSubject.next(confirm);
                    }
                }
            };
            this.pagingDialogKey = this.coreDialogService.open(ConfirmDialogComponent, io);
            return pagingValidationSubject;
        }
        return of(true);
    }

    viewModeChanged(mode: RelationsViewModesEnum): void {
        if (mode === this.selectedViewMode) {
            return;
        }

        this.selectedViewMode = mode;

        if (mode === RelationsViewModesEnum.MAP) {
            this.setMapView();
        } else {
            this.setCardView();
        }
    }

    saveSearch() {
        this.targetingSearchesService.trackSearchEvent(TargetingSearchPendoEventEnum.SAVE, this.targetingSearch);

        this.targetingSearchesService.getValidationErrors(this.targetingSearch, true).pipe(
            switchMap((errors) => {
                this.errors = errors;
                if (!this.errors.hasNoSearchName && !this.errors.searchNameDuplicate) {
                    return this.targetingSearchesService.update(this.targetingSearch);
                }
                return of(null);
            })
        ).subscribe();
    }

    openCreateDialog() {
        this.targetingSearchesService.openCreateDialog(this.targetingSearch, true);
    }

    addTo() {
        if (this.addToType === this.targetingAddToTypes.LIST) {
            this.addToList();
        } else {
            this.addToDistribution();
        }
    }

    addToList() {
        this.listPickerService.setSelectedEntities(this.selectedResults);
        if (this.selectedListsChips?.length > 0) {
            this.listPickerService.addSelectedEntitiesToWorkingLists();
        } else {
            this.openListPicker();
        }
    }

    addToDistribution() {
        this.distributionPickerService.setSelectedEntities(this.selectedResults);
        if (this.selectedDistributionsChips?.length > 0) {
            this.distributionPickerService.addSelectedEntitiesToWorkingDists();
        } else {
            this.openDistPicker();
        }
    }

    confirmRemoveFromList() {
        const  action = this.localizationService.transform('Discover.batchActionsConfirmation.remove', {name: this.targetingList.name, entity: 'contacts'});
        const dialogIO = {
            inputs: {
                message: 'Discover.batchActionsConfirmation.confirm',
                messageParameters: {action},
                itemCountLabel: 'Discover.batchActions.contactsSelected',
                okLabel: 'Discover.targeting.actions.removeFromList.button',
                itemCount: this.selectedResults.length
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.removeFromListLoading = true;
                        if (this.allSelected) {
                            const query = <TargetingQueryApi> {
                                filterQuery : this.targetingSearchService.generateFiltersSearch().toApiModel().query
                            };
                            this.targetingListService.removeEntitiesFromList(null, query, this.total).subscribe();
                        } else {
                            this.targetingListService.removeEntitiesFromList(this.selectedResults).subscribe();
                        }
                    }
                }
            }
        };
        this.coreDialogService.open(BulkActionConfirmationDialogComponent, dialogIO);
    }

    addItemToLists(selectedResult: TargetingResultModel) {
        const listIds = this.selectedLists.map(list => list.id);
        this.targetingListService.addEntitiesToLists(listIds, [selectedResult.toEntityIdModel()]).pipe(
            catchError((e) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.listBuilder.error.addToListMessageSummary',
                    detail: 'Discover.listBuilder.error.addToListMessage'
                });
                return throwError(() => new Error(e));
            })
        ).subscribe(() => {
            for (const list of this.selectedLists) {
                if (selectedResult.listMemberships && !selectedResult.listMemberships.find(x => x.id === list.id)) {
                    selectedResult.listMemberships.push(new TargetingList({
                        id: list.id,
                        name: list.name
                    }));
                }
            }
            this.messageService.add({
                severity: messageSeverityEnum.success,
                summary: 'Discover.listBuilder.addToListSuccessMessageSummary',
                detail: this.selectedLists.length === 1 ?
                    this.localizationService.transform('Discover.listBuilder.addToListSuccessMessage', {
                        count: 1,
                        listName: this.selectedLists[0].name
                    })
                    : this.localizationService.transform('Discover.listBuilder.addToListsSuccessMessage', {
                        contactCount: 1,
                        listCount: this.selectedLists.length
                    })
            });
        });
    }
    addItemToDistributions(selectedResult: TargetingResultModel) {
        this.distributionPickerService.addEntitiesToDists([selectedResult], this.selectedDistributions);
    }

    removeItemFromList(selectedResult: TargetingResultModel) {
        this.removeFromListLoading = true;
        this.targetingListService.removeEntitiesFromList([selectedResult]).subscribe();
    }

    removeItemFromDist(selectedResult: TargetingResultModel) {
        this.removeFromListLoading = true;
        this.targetingListService.removeEntitiesFromDist(
            this.selectedDistributions.map(dist => dist.individualRecipientsListId),
            [selectedResult],
            selectedResult.removeDistributionTooltips
        ).subscribe();
    }

    openListPicker(addMode: boolean = true) {
        this.coreDialogService.open(ListPickerDialogComponent, {
            inputs: {
                addMode: addMode
            },
            outputs: {}
        },
        this.injector);
    }

    openDistPicker(addMode: boolean = true) {
        this.coreDialogService.open(DistPickerDialogComponent, {
            inputs: {
                addMode: addMode
            },
            outputs: {}
        },
        this.injector);
    }

    openBatchActions(action?: TargetingBatchActionsEnum) {
        let sourceType: SourceType;
        let sourceId: number;
        let entityType: EntityType = EntityType.CONTACT;
        switch (this.currentRoute.type) {
            case DiscoverRouteTypeEnum.privateContacts:
                sourceType = SourceType.allPrivateContacts;
                break;
            case DiscoverRouteTypeEnum.targetingListSearchResult:
                sourceType = SourceType.lists;
                sourceId = this.targetingList.id;
                break;
            case DiscoverRouteTypeEnum.portfolioResults:
                sourceType = SourceType.portfolios;
                sourceId = this.targetingList.id;
                break;
            default:
                sourceId = this.targetingSearch.id;
                sourceType = SourceType.searches;
                entityType = this.targetingSearch.searchMode === SearchModeEnum.contact ?
                    EntityType.CONTACT: EntityType.OUTLET;
        }

        this.coreDialogService.open(TargetingBatchActionsDialogComponent, {
            inputs: {
                selectedEntities: this.selectedResults,
                resultsCount: this.allSelected ? this.total : this.selectedResults.length,
                allResultsSelected: this.allSelected,
                initialSourceType: sourceType,
                initialSourceId: sourceId,
                listBuilderLists: this.selectedLists,
                id: 'targetingBatchActionsDialog',
                entityMode: entityType,
                preSelectedBatchActionValue: action
            },
            outputs: {}
        });
    }

    selectAllInPage(): void {
        // It's null for private contacts view
        if (this.targetingSearch && this.targetingSearch.searchMode === SearchModeEnum.outlet) {
            this.selectedResults = [];
            this.results.forEach(outlet => {
                this.selectedResults.push(outlet);
            });
        } else {
            this.selectedResults = this.results.slice();
        }
    }

    selectNone(): void {
        this.listPickerService.setAllResultsSelected(false);
        this.distributionPickerService.setAllResultsSelected(false);
        this.allSelected = false;
        this.selectedResults = [];
    }

    toggleSelectAll(): void {
        if (this.results.length <= this.selectedResults.length) {
            this.selectNone();
        } else {
            this.selectAllInPage();
        }
    }

    clearFilters() {
        this.filterChipsComponent.chipContainer.chipPopover.hide();
        this.targetingSearchService.setFiltersLocked(false);
        this.targetingSearchService.clearFilters();
        this.targetingSearchService.applyFilters();
    }

    clearListChips() {
        this.listChipsComponent.chipPopover.hide();
        this.listPickerService.clearWorkingLists();
    }
    clearDistributionChips() {
        this.listChipsComponent.chipPopover.hide();
        this.distributionPickerService.clearWorkingDistributions();
        this.hideShowRemoveDistribution();
    }

    createContact() {
        this.contactService.createBlank();
        this.contactEditorVisible = true;
    }

    selectAllResults() {
        this.listPickerService.setAllResultsSelected(true, this.total);
        this.distributionPickerService.setAllResultsSelected(true, this.total);
        this.allSelected = true;
    }

    setCardView() {
        if (this.leafletMap && !!this.oldGeocodeResults.length) {
            // Removing the map and all event listeners form map which can cause unwanted page refresh
            this.leafletMap.remove();
        }
        this.targetingSearchService.mapView = false;
        this.targetingSearchService.rectangleQuery = undefined;
        this.oldGeocodeResults = null;
        this.targetingSearchService.doSearch();
    }

    setMapView() {
        this.targetingSearchService.mapView = true;
        this.targetingSearchService.rectangleQuery =  {
            lowerLeftCorner: '-90,-180',
            upperRightCorner: '90,180'
        };
        this.oldGeocodeResults = null;
        this.targetingSearchService.doSearch();
    }


    /**
     * `leaflet.markercluster` patches the global `window.L` (it has no `require('leaflet')` of its
     * own), not the ES namespace object captured by `import * as L from 'leaflet'` above — so
     * plugin-added members (`markerClusterGroup`, `MarkerCluster`, ...) are missing from that
     * namespace at runtime in a bundled `ng build` output, even though they type-check fine and
     * work under `ng serve`. Read them off the global instead.
     */
    private get leafletGlobal(): typeof L {
        return (window as unknown as { L: typeof L }).L;
    }

    initializeMap() {
        if (!this.mapContainer || !this.targetingSearchService.mapView || !this.geocodeResults || this.geocodeResults.length === 0) {
            return;
        }
        const fontAwesomeIcon = this.mapMarkerTemplate(1);

        if (this.leafletMap?.hasLayer(this.leafletMarkers)) {
            this.leafletMap.removeLayer(this.leafletMarkers);
            this.leafletMap.remove();
        }

        // Initial view
        this.leafletMap = new L.Map(this.mapContainer.nativeElement, {
            worldCopyJump: true,
            maxBounds: this.MAP_MAX_BOUNDS,
            zoomControl: false
        }).setView([51.505, -0.09], 5);
        this.leafletMap.setMaxZoom(this.MAP_MAX_ZOOM);
        this.leafletMap.setMinZoom(1);

        // Removes title tooltip from zoom controls, adds zoom controls to map
        L.control.zoom({ zoomInTitle: '', zoomOutTitle: '' }).addTo(this.leafletMap);

        this.leafletMap.on('zoomend', function(event) {
            this.adjustMapViewport();
        }.bind(this));

        this.leafletMap.on('moveend', function(event) {
            if (this.showMapResetButton === null) {
                this.showMapResetButton = false;
            } else {
                this.showMapResetButton = true;
            }

            const boundsAfterMove: L.LatLngBounds = this.leafletMap.getBounds();
            this.targetingSearchService.setPageCriteria({page: 1, pageSize: 25}, false);
            this.performMapSearch(boundsAfterMove.getSouthWest(), boundsAfterMove.getNorthEast());
        }.bind(this));

        this.leafletMarkers = this.leafletGlobal.markerClusterGroup({
            animate: false,
            zoomToBoundsOnClick: false,
            spiderfyOnMaxZoom: false,
            iconCreateFunction: function(cluster) {
                return this.mapMarkerTemplate(cluster.getChildCount());
            }.bind(this)
        });

        this.leafletMarkers.on('clusterclick', (e) => {
            const {_southWest, _northEast} = e.propagatedFrom.getBounds();
            this.targetingSearchService.setPageCriteria({page: 1, pageSize: 25}, false);
            this.performMapSearch(_southWest, _northEast);

            // mark map node as selected style
            e.propagatedFrom.setIcon(L.divIcon({
                html: '<span class="fa fas fa-map-marker fa-4x" aria-hidden="true"></span><span class="agpr-mapmarker-iconSelected-number">'
                        + this.cleanNumber(e.propagatedFrom.getChildCount()) + '</span>',
                iconSize: [20, 20],
                className: 'agpr-mapmarker-iconSelected'
            }));
        });


        this.leafletMarkers.on('click', (e) => {
            const pointCoords = e.propagatedFrom._latlng;
            this.targetingSearchService.setPageCriteria({page: 1, pageSize: 25}, false);
            this.performMapSearch(pointCoords, pointCoords);

            // mark map node as selected style
            e.propagatedFrom.setIcon(L.divIcon({
                html: '<span class="fa fas fa-map-marker fa-4x" aria-hidden="true"></span><span class="agpr-mapmarker-iconSelected-number">1</span>',
                iconSize: [20, 20],
                className: 'agpr-mapmarker-iconSelected'
            }));
        });

        this.geocodeResults.forEach(geocode => {
            this.leafletMarkers.addLayer(L.marker(geocode, { icon: fontAwesomeIcon}));
        });

        this.leafletMap.addLayer(this.leafletMarkers);


        const tileLayer = L.tileLayer(this.configService.getConfig().apiUrl + '/{id}/tiles/{z}/{x}/{y}', {
            attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery ©'
                + '<a href="https://www.mapbox.com/">Mapbox</a>',
            maxZoom: this.MAP_MAX_ZOOM,
            id: 'mapbox/streets-v11',
            tileSize: 512,
            zoomOffset: -1,
            // key from :
            // https://account.mapbox.com/
            // account : agilityprmaps
            // password : contact Jarrod
        }).addTo(this.leafletMap);

        tileLayer.on('tileerror', (e) => {
            this.rateLimitExceeded = true;
        });

        // https://stackoverflow.com/questions/36246815/data-toggle-tab-does-not-download-leaflet-map/36257493#36257493
        setTimeout(() => {
            this.leafletMap.invalidateSize();
            this.leafletMap.fitBounds(this.leafletMarkers.getBounds(), {maxZoom: this.MAP_MAX_ZOOM});
        }, 100);
    }

    performMapSearch(southWest: L.LatLng, northEast: L.LatLng) {
        this.cleanMapNodeStyles();

        // set invalid bounds to closest valid bounds
        for (const bound of [southWest, northEast]) {
            if (bound.lat <= -90) {
                bound.lat = -89.99999;
            } else if (bound.lat >= 90) {
                bound.lat = 89.99999;
            } else if (bound.lng <= -180) {
                bound.lng = -179.99999;
            } else if (bound.lng >= 180) {
                bound.lng = 179.99999;
            }
        }

        this.targetingSearchService.rectangleQuery = {
            lowerLeftCorner: `${(southWest.lat - (1e-5)).toFixed(6)},${(southWest.lng - (1e-5)).toFixed(6)}`,
            upperRightCorner:  `${(northEast.lat + (1e-5)).toFixed(6)},${(northEast.lng + (1e-5)).toFixed(6)}`
        };

        this.targetingSearchService.doSearch(undefined, undefined, false);
    }

    /**
     * remove map marker highlighting ("selected") style from leaflet map
     */
    cleanMapNodeStyles(): void {
        this.leafletMap.eachLayer((layer: L.Layer) => {

            if (layer instanceof this.leafletGlobal.MarkerCluster && layer?.options?.icon?.options?.className === 'agpr-mapmarker-iconSelected') {
                layer.setIcon(L.divIcon({
                    html:
                    '<span class="fa-solid fa-location-pin agpr-mapmarker-iconUnselected-location"></span><span class="agpr-mapmarker-iconUnselected-number">'
                            + this.cleanNumber(layer.getChildCount()) + '</span>',
                    iconSize: [20, 20],
                    className: 'agpr-mapmarker-iconUnselected'
                }));
            } else if (layer instanceof L.Marker && layer?.options?.icon?.options?.className === 'agpr-mapmarker-iconSelected') {
                layer.setIcon(L.divIcon({
                    html:
                    `<span class="fa-solid fa-location-pin agpr-mapmarker-iconUnselected-location">
                    </span><span class="agpr-mapmarker-iconUnselected-number">1</span>`,
                    iconSize: [20, 20],
                    className: 'agpr-mapmarker-iconUnselected'
                }));
            }
        });
    }

    adjustMapViewport(): L.LatLngBounds {
        const currentZoom = this.leafletMap.getZoom();
        const boundsAfterZoom = this.leafletMap.getBounds();

        let adjustmentNecessary = false;

        let finalBounds: L.LatLngBounds;

        for (const markerLocation of this.geocodeResults) {
            if (boundsAfterZoom.contains(markerLocation)) {
                if (finalBounds) {
                    finalBounds.extend(markerLocation);
                } else {
                    finalBounds = L.latLngBounds(markerLocation, markerLocation);
                }
                this.excludedMarkers.delete(markerLocation);
            } else {
                if (!this.excludedMarkers.has(markerLocation)) {
                    this.excludedMarkers.add(markerLocation);
                    adjustmentNecessary = true;
                }
            }
        };

        // We zoomed in an area that has no points at all.
        // Let's take the user back to the nearest marker.
        if (!finalBounds) {
            let minDist = 5000000000;
            for (const markerLocation of this.geocodeResults) {
                const mapCenter = this.leafletMap.getCenter();
                const distanceToCenter = mapCenter.distanceTo(markerLocation);
                if (distanceToCenter < minDist) {
                    minDist = distanceToCenter;
                    finalBounds = L.latLngBounds(markerLocation, markerLocation);
                }
            }
        }
        if (adjustmentNecessary) {
            this.leafletMap.fitBounds(finalBounds, {maxZoom: currentZoom});
        }

        return finalBounds;
    }

    resetZoom() {
        this.leafletMap.setView([0, 0], this.leafletMap.getMinZoom());
        this.showMapResetButton = null;
    }

    toLeafletLatLong(geocode: string): L.LatLng {
        const latlong = geocode.split(',');
        return new L.LatLng(+latlong[0], +latlong[1]);
    }

    mapMarkerTemplate(number: number): L.DivIcon {

        return L.divIcon({
            html: '<span class="fa fas fa-map-marker fa-4x" aria-hidden="true"></span><span class="agpr-mapmarker-iconUnselected-number">'
            + this.cleanNumber(number) + '</span>',
            iconSize: [20, 20],
            className: 'agpr-mapmarker-iconUnselected'
        });
    }

    /**
     * Convert number to string, simplify with k notation
     */
    cleanNumber(number: number): string {
        let numberStr = number.toString();
        if (number >= 1000) {
            numberStr = numberStr.substring(0, 1) + 'k+';
        }
        return numberStr;
    }

    deleteSelected() {
        if (this.allSelected) {
            const query = <TargetingQueryApi> {
                filterQuery : this.targetingSearchService.generateFiltersSearch().toApiModel().query
            };
            this.contactService.deleteContacts(null, query, this.total);
        } else {
            this.contactService.deleteContacts(this.selectedResults);
        }
    }

    cardFocused(entity: TargetingResultModel) {
        this.targetingSearchService.setFocusedResult(entity);
    }

    onContactEditorClose(id: string) {
        this.contactEditorVisible = false;
        this.contactService.clearEditingContact();
    }

    // There will be variants of this method depending which tab it comes from and which
    // data is already loaded
    spawnTakeover(event: EntityTakeoverModel) {
        const dialog = this.targetingRhpDialogService.openTakeOver({
            inputs: {
                contactId: event.contactId,
                outletId: event.outletId,
                entityType: event.entityType,
                isPrivate: event.isPrivate
            }
        });
    }

    updateListBuilder() {
        this.listPickerService.setWorkingLists(this.selectedLists);
        this.selectedListsChips = this.selectedLists.map(workingList => ({
            label: workingList.name
        }));
        this.listPickerService.setWorkingListsLock(true);
        this.updateSelectedListsTooltip();
    }

    updateDistributionBuilder() {
        this.distributionPickerService.setWorkingDistributions(this.selectedDistributions);
        this.selectedDistributionsChips = this.selectedDistributions.map(workingDistribution => ({
            label: workingDistribution.name
        }));
        this.distributionPickerService.setWorkingDistributionsLock(true);
        this.updateSelectedDistributionsTooltip();

        this.hideShowRemoveDistribution();
    }

    removeDistribution($event) {
        this.distributionPickerService.removeWorkingDistributionByIndex($event);
        this.hideShowRemoveDistribution();
    }

    updateSelectedListsTooltip() {
        if (!this.selectedLists.length) {
            this.selectedListsTooltip = null;
            return;
        }
        if (this.selectedLists.length > 1) {
            let listNames = '';
            this.selectedLists.forEach((list, index) => {
                if (index < this.selectedLists.length - 1) {
                    listNames += list.name + ', ';
                } else {
                    listNames += list.name;
                }
            });
            this.selectedListsTooltip = this.localizationService.transform('Discover.targeting.actions.addToLists', {lists: listNames});
        } else {
            this.selectedListsTooltip = this.localizationService.transform('Discover.targeting.actions.addToList', {list: this.selectedLists[0].name});
        }
    }

    updateSelectedDistributionsTooltip() {
        if (!this.selectedDistributions.length) {
            this.selectedDistributionsTooltip = null;
            return;
        }
        if (this.selectedDistributions.length > 1) {
            let distributionNames = '';
            this.selectedDistributions.forEach((distribution, index) => {
                if (index < this.selectedDistributions.length - 1) {
                    distributionNames += distribution.name + ', ';
                } else {
                    distributionNames += distribution.name;
                }
            });
            // eslint-disable-next-line max-len
            this.selectedDistributionsTooltip = this.localizationService.transform('Discover.targeting.actions.addToDistributions', {distributions: distributionNames});
        } else {
            // eslint-disable-next-line max-len
            this.selectedDistributionsTooltip = this.localizationService.transform('Discover.targeting.actions.addToDistribution', {distribution: this.selectedDistributions[0].name});
        }
    }

    contactEmploymentChange(change: EmploymentChange) {
        if (!this.targetingList) {
            // Employment changes should only be saved for targeting list contexts
            return;
        }

        if (change.activeNoticeChange) {
            this.updateActiveNotices();
        }

        this.targetingListService.updateListContactEmployment(this.targetingList.id, change.contact).subscribe();
    }

    updateActiveNotices() {
        this.listHasActiveNotices = !!this.results?.some(result => result.hasActiveNotice);
    }

    applyActiveNoticeFilter() {
        // TODO: connect to filter logic
        this.targetingSearchService.applyActiveNoticeFilter();
    }

    selectTargetingType() {
        this.listPickerService.clearWorkingLists();
        this.distributionPickerService.clearWorkingDistributions();
    }

    hideShowRemoveDistribution() {
        this.results?.forEach(result => result.hideShowRemoveDistribution(this.selectedDistributions));
    }

    onAnimationStart(event: AnimationEvent) {
        this.toolbarStateService.onAnimationStart(event);
    }

    onAnimationDone(event: AnimationEvent) {
        this.toolbarStateService.onAnimationDone(event);
    }

    onScroll(scrollTop: number) {
        this.toolbarStateService.onScroll(scrollTop);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.targetingSearchService.registerPagingValidator(null);
    }
}
